/**
 * CompanySelectField Component
 * Reusable company selection field that auto-hides based on the active context
 *
 * Auto-hides and auto-fills when the active context is a company: there is nothing to choose,
 * because the context already says which one. O mesmo vale quando a pessoa alcança uma
 * empresa só — a clínica que atende uma —, e por isso o campo espera a lista chegar antes
 * de se decidir por aparecer.
 */

import React, { useEffect } from 'react';
import {
  Autocomplete,
  TextField,
  FormControl,
  CircularProgress,
  Grid,
} from '@mui/material';
import { useCompanyContext } from '../../hooks/useCompanyContext';
import { useCompanyList } from '../../stores/companyStore';
import { Company } from '../../types';

interface CompanySelectFieldProps {
  /** Current field value (company ID as string) */
  value: string;
  /** Callback when value changes */
  onChange: (value: string) => void;
  /** Whether the field has a validation error */
  error?: boolean;
  /** Helper text to display (usually error message) */
  helperText?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is required (affects label) */
  required?: boolean;
  /** Whether to hide the field in edit mode (default: true) */
  hideOnEdit?: boolean;
  /**
   * Tamanho da célula de grade, quando o campo mora num formulário em colunas.
   *
   * Existe porque o campo se esconde sozinho, e quem se esconde precisa levar a própria célula
   * junto: o pai que desenha a célula não sabe da regra, e a célula vazia ficava ocupando a
   * coluna — um buraco no meio do formulário em vez de os campos se reorganizarem.
   */
  grid?: { xs?: number; sm?: number; md?: number; lg?: number };
  /** Whether the form is in edit mode */
  isEditMode?: boolean;
  /** Custom label for the field */
  label?: string;
}

/**
 * Company selection field with auto-hide functionality
 *
 * @example
 * ```tsx
 * <Controller
 *   name="companyId"
 *   control={control}
 *   render={({ field }) => (
 *     <CompanySelectField
 *       value={field.value}
 *       onChange={field.onChange}
 *       error={!!errors.companyId}
 *       helperText={errors.companyId?.message}
 *       isEditMode={mode === 'edit'}
 *     />
 *   )}
 * />
 * ```
 */
export const CompanySelectField: React.FC<CompanySelectFieldProps> = ({
  value,
  onChange,
  error,
  helperText,
  disabled,
  required = true,
  hideOnEdit = true,
  isEditMode = false,
  label = 'Empresa',
  grid,
}) => {
  const { shouldShowCompanyField, effectiveCompanyIdString } = useCompanyContext();
  const { companies, carregada, isLoading, loadCompanies } = useCompanyList();

  // Busca enquanto não houver resposta para este contexto — e não enquanto a lista estiver
  // vazia: vazia é uma resposta possível, e reperguntá-la não muda nada.
  useEffect(() => {
    if (!carregada && !isLoading) {
      loadCompanies();
    }
  }, [carregada, isLoading, loadCompanies]);

  // Auto-fill when field is hidden
  useEffect(() => {
    if (!shouldShowCompanyField && effectiveCompanyIdString && !value) {
      onChange(effectiveCompanyIdString);
    }
  }, [shouldShowCompanyField, effectiveCompanyIdString, value, onChange]);

  /**
   * Uma empresa alcançável é uma escolha que não existe — a clínica que atende uma só, e a
   * empresa que já é o contexto. Perguntá-la punha um passo obrigatório entre a pessoa e a
   * tela, e nada carregava antes de ela responder o que já estava respondido.
   *
   * A conta só vale depois da resposta: antes dela a lista está vazia por não se saber ainda,
   * e o campo aparecia por um instante antes de se esconder.
   */
  const empresaUnica = carregada && companies.length === 1 ? companies[0] : undefined;

  useEffect(() => {
    if (empresaUnica && !value) {
      onChange(String(empresaUnica.id));
    }
  }, [empresaUnica, value, onChange]);

  // Hide in edit mode if configured
  if (hideOnEdit && isEditMode) {
    return null;
  }

  // Hide when user has single company or header selection
  if (!shouldShowCompanyField) {
    return null;
  }

  // Enquanto não há resposta não há o que desenhar: o campo que aparece para sumir em
  // seguida se lê como defeito, e a espera é curta.
  if (!carregada || empresaUnica) {
    return null;
  }

  // Find the selected company object
  const selectedCompany = companies.find(
    (c: Company) => c.id === value || c.id === Number(value)?.toString()
  ) || null;

  const campo = (
    <FormControl fullWidth error={error}>
      <Autocomplete
        options={companies}
        getOptionLabel={(option: Company | string) =>
          typeof option === 'string'
            ? option
            : option.tradeName || option.legalName || ''
        }
        // Sem isto o MUI usa o rótulo como chave, e duas empresas de mesmo nome
        // colidem — a lista passa a renderizar de forma imprevisível.
        getOptionKey={(option: Company | string) =>
          typeof option === 'string' ? option : option.id
        }
        loading={isLoading}
        disabled={disabled}
        value={selectedCompany}
        onChange={(_, val: Company | null) => onChange(val?.id || '')}
        isOptionEqualToValue={(option: Company, val: Company) =>
          option.id === val?.id
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={`${label}${required ? ' *' : ''}`}
            error={error}
            helperText={helperText}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading && <CircularProgress color="inherit" size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </FormControl>
  );

  return grid ? <Grid size={grid}>{campo}</Grid> : campo;
};

export default CompanySelectField;
